import { Minus, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

interface QuantityStepperProps {
  value: number
  onDecrease: () => void
  onIncrease: () => void
  min?: number
  max?: number
  className?: string
  size?: 'sm' | 'md'
  tone?: 'dark' | 'light'
  /** Matches product card asymmetric corners (top-left + bottom-right). */
  shape?: 'default' | 'product-card' | 'pill'
}

export function QuantityStepper({
  value,
  onDecrease,
  onIncrease,
  min = 1,
  max,
  className,
  size = 'md',
  tone = 'dark',
  shape = 'default',
}: QuantityStepperProps) {
  const btnSize = size === 'sm' ? 'p-1' : 'p-1.5'
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'
  const valueWidth = size === 'sm' ? 'w-6' : 'w-8'
  const isLight = tone === 'light'
  const isProductCard = shape === 'product-card'
  const isPill = shape === 'pill'

  return (
    <div
      className={cn(
        'inline-flex items-center border',
        isPill && 'overflow-hidden rounded-full',
        !isProductCard && !isPill && 'rounded-lg',
        isProductCard && 'product-card-qty-stepper',
        isLight
          ? 'border-stone-200 bg-white shadow-sm'
          : 'border-luxury-600 bg-luxury-800/50',
        className
      )}
      role="group"
      aria-label="Quantity"
    >
      <button
        type="button"
        className={cn(
          btnSize,
          'transition-colors focus-ring',
          !isProductCard && !isPill && 'rounded-l-lg',
          isLight
            ? 'text-zinc-500 hover:bg-stone-50 hover:text-gold-700'
            : 'text-zinc-400 hover:text-gold-700'
        )}
        onClick={onDecrease}
        disabled={value <= min}
        aria-label="Decrease quantity"
      >
        <Minus className={iconSize} />
      </button>
      <span
        className={cn(
          valueWidth,
          'text-center text-xs tabular-nums sm:text-sm',
          isLight ? 'text-zinc-800' : 'text-zinc-800'
        )}
      >
        {value}
      </span>
      <button
        type="button"
        className={cn(
          btnSize,
          'transition-colors focus-ring',
          !isProductCard && !isPill && 'rounded-r-lg',
          isLight
            ? 'text-zinc-500 hover:bg-stone-50 hover:text-gold-700'
            : 'text-zinc-400 hover:text-gold-700'
        )}
        onClick={onIncrease}
        disabled={max !== undefined && value >= max}
        aria-label="Increase quantity"
      >
        <Plus className={iconSize} />
      </button>
    </div>
  )
}
